import React, { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { GamingSegment, SEGMENT_TYPE } from "../../constants/types";

interface SpinnerWheelProps {
  segments: GamingSegment[];
  onSpinComplete: (segment: GamingSegment) => void;
  winningSegmentId?: string;
  disabled?: boolean;
  size?: number;
  isLoading?: boolean;
}

const SpinnerWheel: React.FC<SpinnerWheelProps> = ({
  segments,
  onSpinComplete,
  winningSegmentId,
  disabled = false,
  size = 320,
  isLoading = false,
}) => {
  const { t } = useTranslation("clientView");
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [loadingRotation, setLoadingRotation] = useState(0);
  const wheelRef = useRef<SVGSVGElement>(null);
  const spinTimeoutRef = useRef<NodeJS.Timeout>();
  const loadingIntervalRef = useRef<NodeJS.Timeout>();
  const RE_ORDER_SEGMENT_FROM = 7;

  useEffect(() => {
    if (isLoading && !winningSegmentId) {
      loadingIntervalRef.current = setInterval(() => {
        setLoadingRotation((prev) => prev + 15);
      }, 50);
    } else {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
      }
    }

    return () => {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
      }
    };
  }, [isLoading, winningSegmentId]);

  const segmentAngle = 360 / segments.length;
  const radius = size / 2;
  const centerX = radius;
  const centerY = radius;
  const innerRadius = radius * 0.15;

  useEffect(() => {
    return () => {
      if (spinTimeoutRef.current) {
        clearTimeout(spinTimeoutRef.current);
      }
    };
  }, []);

  const getSegmentPath = (index: number): string => {
    const startAngle = index * segmentAngle - 90;
    const endAngle = startAngle + segmentAngle;

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = centerX + (radius - 5) * Math.cos(startRad);
    const y1 = centerY + (radius - 5) * Math.sin(startRad);
    const x2 = centerX + (radius - 5) * Math.cos(endRad);
    const y2 = centerY + (radius - 5) * Math.sin(endRad);

    const x3 = centerX + innerRadius * Math.cos(endRad);
    const y3 = centerY + innerRadius * Math.sin(endRad);
    const x4 = centerX + innerRadius * Math.cos(startRad);
    const y4 = centerY + innerRadius * Math.sin(startRad);

    const largeArc = segmentAngle > 180 ? 1 : 0;

    return `
      M ${x1} ${y1}
      A ${radius - 5} ${radius - 5} 0 ${largeArc} 1 ${x2} ${y2}
      L ${x3} ${y3}
      A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4}
      Z
    `;
  };

  const getTextPosition = (index: number, hasImage: boolean) => {
    const angle = index * segmentAngle + segmentAngle / 2 - 90;
    const rad = (angle * Math.PI) / 180;
    const textRadius = hasImage
      ? segments.length <= RE_ORDER_SEGMENT_FROM
        ? radius * 0.83
        : radius * 0.4
      : radius * 0.65;
    return {
      x: centerX + textRadius * Math.cos(rad),
      y: centerY + textRadius * Math.sin(rad),
      rotation: segments.length <= RE_ORDER_SEGMENT_FROM ? angle + 90 : angle,
    };
  };

  const getImagePosition = (index: number) => {
    const angle = index * segmentAngle + segmentAngle / 2 - 90;
    const rad = (angle * Math.PI) / 180;
    const imageRadius =
      segments.length <= RE_ORDER_SEGMENT_FROM ? radius * 0.4 : radius * 0.75;
    return {
      x: centerX + imageRadius * Math.cos(rad),
      y: centerY + imageRadius * Math.sin(rad),
      rotation: angle + 90,
    };
  };

  const spin = useCallback(() => {
    if (isSpinning || disabled || !winningSegmentId) return;

    setIsSpinning(true);

    const winningIndex = segments.findIndex((s) => s.id === winningSegmentId);
    if (winningIndex === -1) {
      setIsSpinning(false);
      return;
    }

    const segmentCenterAngle = winningIndex * segmentAngle + segmentAngle / 2;

    const baseRotations = Math.floor(5 + Math.random() * 3);
    const targetRotation = baseRotations * 360 + (360 - segmentCenterAngle);

    setRotation(targetRotation);

    spinTimeoutRef.current = setTimeout(() => {
      setIsSpinning(false);
      const winningSeg = segments[winningIndex];
      onSpinComplete(winningSeg);
    }, 5000);
  }, [
    isSpinning,
    disabled,
    winningSegmentId,
    segments,
    segmentAngle,
    rotation,
    onSpinComplete,
  ]);

  useEffect(() => {
    if (winningSegmentId && !isSpinning) {
      spin();
    }
  }, [winningSegmentId]);

  return (
    <div className="flex flex-col items-center gap-4" translate="no">
      <div className="relative" style={{ width: size, height: size }}>
        <div
          className="absolute z-20 left-1/2 -translate-x-1/2 -top-2"
          style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))" }}
        >
          <svg width="30" height="40" viewBox="0 0 30 40">
            <polygon
              points="15,40 0,10 15,0 30,10"
              fill="#1f2937"
              stroke="#fff"
              strokeWidth="2"
            />
          </svg>
        </div>

        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            padding: "8px",
            boxShadow:
              "0 10px 40px rgba(102, 126, 234, 0.4), inset 0 2px 10px rgba(255,255,255,0.2)",
          }}
        >
          <div
            className="w-full h-full rounded-full bg-gray-900"
            style={{ padding: "4px" }}
          >
            <svg
              ref={wheelRef}
              width={size - 24}
              height={size - 24}
              viewBox={`0 0 ${size} ${size}`}
              className="rounded-full"
              style={{
                transform: `rotate(${
                  isLoading && !winningSegmentId ? loadingRotation : rotation
                }deg)`,
                transition: isSpinning
                  ? "transform 5s cubic-bezier(0.17, 0.67, 0.12, 0.99)"
                  : "none",
              }}
            >
              {segments.map((segment, index) => {
                const hasImage = !!segment.image_url;
                const imageSize = Math.max(
                  28,
                  Math.min(42, 55 - segments.length * 2)
                );
                const imagePos = getImagePosition(index);
                const textPos = getTextPosition(index, hasImage);

                return (
                  <g key={segment.id}>
                    <path
                      d={getSegmentPath(index)}
                      fill={segment.color}
                      stroke="#fff"
                      strokeWidth="2"
                      className="transition-opacity"
                      style={{
                        filter:
                          segment.type === SEGMENT_TYPE.PRIZE
                            ? "brightness(1.05)"
                            : "brightness(0.95)",
                      }}
                    />
                    <text
                      x={textPos.x}
                      y={textPos.y}
                      fill="#fff"
                      fontSize={
                        hasImage
                          ? Math.max(10, 14 - segments.length / 2)
                          : Math.max(12, 18 - segments.length / 2)
                      }
                      fontWeight="bold"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      transform={`rotate(${textPos.rotation}, ${textPos.x}, ${textPos.y})`}
                      style={{
                        textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                        pointerEvents: "none",
                      }}
                    >
                      {(() => {
                        const text = segment.display_text;
                        const maxChars = 25;
                        const lineMaxChars = 10;

                        if (text.length <= lineMaxChars) {
                          return text;
                        }

                        const truncatedText =
                          text.length > maxChars
                            ? text.substring(0, maxChars - 3) + "..."
                            : text;

                        const midPoint = Math.ceil(truncatedText.length / 2);
                        let splitIndex = truncatedText.lastIndexOf(
                          " ",
                          midPoint
                        );

                        if (splitIndex === -1) {
                          splitIndex = truncatedText.indexOf(" ", midPoint);
                        }

                        if (splitIndex === -1) {
                          return truncatedText;
                        }

                        const line1 = truncatedText.substring(0, splitIndex);
                        const line2 = truncatedText.substring(splitIndex + 1);

                        return (
                          <>
                            <tspan x={textPos.x} dy="-0.5em">
                              {line1}
                            </tspan>
                            <tspan x={textPos.x} dy="1em">
                              {line2}
                            </tspan>
                          </>
                        );
                      })()}
                    </text>

                    {hasImage && (
                      <g
                        transform={`rotate(${imagePos.rotation}, ${imagePos.x}, ${imagePos.y})`}
                      >
                        <defs>
                          <clipPath id={`clip-${segment.id}`}>
                            <circle
                              cx={imagePos.x}
                              cy={imagePos.y}
                              r={imageSize / 2}
                            />
                          </clipPath>
                        </defs>
                        <circle
                          cx={imagePos.x}
                          cy={imagePos.y}
                          r={imageSize / 2 + 4}
                          fill="rgba(255,255,255,0.3)"
                          style={{ filter: "blur(2px)" }}
                        />
                        <circle
                          cx={imagePos.x}
                          cy={imagePos.y}
                          r={imageSize / 2 + 2}
                          fill="#fff"
                          style={{
                            filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.4))",
                          }}
                        />
                        <image
                          href={segment.image_url}
                          x={imagePos.x - imageSize / 2}
                          y={imagePos.y - imageSize / 2}
                          width={imageSize}
                          height={imageSize}
                          clipPath={`url(#clip-${segment.id})`}
                          preserveAspectRatio="xMidYMid slice"
                        />
                      </g>
                    )}
                  </g>
                );
              })}

              <circle
                cx={centerX}
                cy={centerY}
                r={innerRadius}
                fill="url(#centerGradient)"
                stroke="#fff"
                strokeWidth="3"
              />

              <defs>
                <radialGradient id="centerGradient">
                  <stop offset="0%" stopColor="#667eea" />
                  <stop offset="100%" stopColor="#764ba2" />
                </radialGradient>
              </defs>
            </svg>
          </div>
        </div>

        {Array.from({ length: 16 }).map((_, i) => {
          const angle = (i * 360) / 16 - 90;
          const rad = (angle * Math.PI) / 180;
          const lightRadius = radius + 2;
          const colors = [
            "#FF6B6B",
            "#FFE66D",
            "#4ECDC4",
            "#A78BFA",
            "#F472B6",
            "#34D399",
            "#FBBF24",
            "#60A5FA",
          ];
          const color = colors[i % colors.length];
          return (
            <div
              key={i}
              className="absolute w-4 h-4 rounded-full flex items-center justify-center"
              style={{
                left: centerX + lightRadius * Math.cos(rad) - 8,
                top: centerY + lightRadius * Math.sin(rad) - 8,
                backgroundColor: "#2d2d2d",
                border: "2px solid #4a4a4a",
                boxShadow:
                  "inset 0 1px 2px rgba(0,0,0,0.5), 0 1px 1px rgba(255,255,255,0.1)",
              }}
            >
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{
                  backgroundColor: color,
                  boxShadow: `0 0 6px ${color}, 0 0 10px ${color}`,
                  animation: "lightOnOff 1s ease-in-out infinite",
                  animationDelay: `${i * 0.0625}s`,
                }}
              />
            </div>
          );
        })}

        <style>{`
          @keyframes lightOnOff {
            0%, 100% {
              opacity: 0.3;
              transform: scale(0.7);
              filter: brightness(0.5);
            }
            50% {
              opacity: 1;
              transform: scale(1.2);
              filter: brightness(1.5);
            }
          }
        `}</style>
      </div>

      {!isSpinning && !winningSegmentId && !disabled && (
        <p className="text-gray-500 text-sm text-center">
          {t("games.spinner.instructions")}
        </p>
      )}
    </div>
  );
};

export default SpinnerWheel;
